import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { PaymentMethod, UnitType } from '@pos/core';
import type { SaleRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { useSyncStatus } from '../../services/sync.ts';

export type Period = 'today' | '7d' | '30d';

export const PERIODS: Record<Period, string> = { today: 'Hoy', '7d': 'Últimos 7 días', '30d': 'Últimos 30 días' };

export function periodStart(period: Period, now = new Date()): string {
  const d = new Date(now);
  if (period === 'today') d.setHours(0, 0, 0, 0);
  else d.setDate(d.getDate() - (period === '7d' ? 7 : 30));
  return d.toISOString();
}

interface ServerSale {
  id: string;
  business_id: string;
  kind: 'sale' | 'return';
  original_sale_id: string | null;
  number: string;
  device_id: string | null;
  seller_id: string | null;
  subtotal: number;
  discount_total: number;
  adjustment_total: number;
  rounding_total: number;
  tax_total: number;
  total: number;
  notes: string | null;
  occurred_at: string;
  created_at: string;
  server_xid: string;
  customer_id: string | null;
  cash_session_id: string | null;
  sale_items: Array<{
    id: string;
    line_no: number;
    variant_id: string;
    description: string;
    qty_milli: number;
    unit_price: number;
    discount: number;
    tax_bps: number;
    net: number;
    tax: number;
    total: number;
    original_item_id: string | null;
  }>;
  sale_payments: Array<{
    id: string;
    method: PaymentMethod;
    base: number;
    adjustment: number;
    rounding: number;
    amount: number;
    tendered: number | null;
    reference: string | null;
    installments: number | null;
  }>;
}

const SELECT = '*, sale_items(*), sale_payments(*)';

/** Venta del servidor → mismo formato que las locales (la unidad sale del catálogo). */
export function fromServer(row: ServerSale, unitOf: (variantId: string) => UnitType): SaleRow {
  return {
    id: row.id,
    business_id: row.business_id,
    kind: row.kind,
    original_sale_id: row.original_sale_id,
    number: row.number,
    device_id: row.device_id,
    seller_id: row.seller_id ?? '',
    subtotal: Number(row.subtotal),
    discount_total: Number(row.discount_total),
    adjustment_total: Number(row.adjustment_total),
    rounding_total: Number(row.rounding_total),
    tax_total: Number(row.tax_total),
    total: Number(row.total),
    notes: row.notes,
    occurred_at: row.occurred_at,
    created_at: row.created_at,
    server_xid: String(row.server_xid),
    customer_id: row.customer_id,
    cash_session_id: row.cash_session_id,
    items: [...row.sale_items]
      .sort((a, b) => a.line_no - b.line_no)
      .map((i) => ({
        id: i.id,
        movement_id: '',
        variant_id: i.variant_id,
        description: i.description,
        unit_type: unitOf(i.variant_id),
        qty_milli: Number(i.qty_milli),
        unit_price: Number(i.unit_price),
        discount: Number(i.discount),
        tax_bps: i.tax_bps,
        net: Number(i.net),
        tax: Number(i.tax),
        total: Number(i.total),
        original_item_id: i.original_item_id,
      })),
    payments: row.sale_payments.map((p) => ({
      id: p.id,
      method: p.method,
      base: Number(p.base),
      adjustment: Number(p.adjustment),
      rounding: Number(p.rounding),
      amount: Number(p.amount),
      tendered: p.tendered === null ? null : Number(p.tendered),
      reference: p.reference,
      installments: p.installments,
    })),
  };
}

export interface SalesQuery {
  from: string;
  /** Busca por número (parte del número, p. ej. "123"). */
  number: string;
}

export interface SalesList {
  sales: SaleRow[];
  /** Ids de ventas que todavía no llegaron al servidor. */
  pending: Set<string>;
  /** null = sin conexión (se muestran solo las de este dispositivo). */
  serverError: string | null;
  loading: boolean;
  online: boolean;
}

/**
 * Ventas del período: las de este dispositivo (siempre, aun sin conexión) más las del servidor
 * (de todos los dispositivos, según lo que cada usuario puede ver).
 */
export function useSalesList(query: SalesQuery, unitOf: (variantId: string) => UnitType, onlyOwn: string | null): SalesList {
  const { business } = useSession();
  const online = useOnline();
  const { lastSyncAt } = useSyncStatus();
  const [server, setServer] = useState<SaleRow[] | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const local = useLiveQuery(
    () => db.sales.where('occurred_at').aboveOrEqual(query.from).filter((s) => s.business_id === business.id).toArray(),
    [business.id, query.from],
  );

  useEffect(() => {
    if (!online) {
      setServer(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    let request = supabase.from('sales').select(SELECT).eq('business_id', business.id).gte('occurred_at', query.from).order('occurred_at', { ascending: false }).limit(300);
    if (query.number.trim()) request = request.ilike('number', `%${query.number.trim()}%`);
    void request.then(({ data, error }) => {
      if (cancelled) return;
      setLoading(false);
      if (error) {
        setServerError('No se pudieron traer las ventas del servidor. Se muestran las de este dispositivo.');
        return;
      }
      setServerError(null);
      setServer((data as unknown as ServerSale[]).map((r) => fromServer(r, unitOf)));
    });
    return () => {
      cancelled = true;
    };
    // unitOf cambia con el catálogo; no hace falta volver a pedir por eso.
  }, [online, business.id, query.from, query.number, lastSyncAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    const byId = new Map<string, SaleRow>();
    for (const s of server ?? []) byId.set(s.id, s);
    const pending = new Set<string>();
    const term = query.number.trim();
    for (const s of local ?? []) {
      if (term && !s.number.includes(term)) continue;
      if (!byId.has(s.id)) {
        byId.set(s.id, s);
        if (s.server_xid === undefined) pending.add(s.id);
      }
    }
    let sales = [...byId.values()];
    if (onlyOwn) sales = sales.filter((s) => s.seller_id === onlyOwn);
    sales.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
    return { sales, pending, serverError: online ? serverError : null, loading: loading && !server, online };
  }, [server, local, query.number, onlyOwn, online, serverError, loading]);
}

/** Devoluciones de una venta: las conocidas en el período más las del servidor (si hay conexión). */
export async function fetchReturns(saleId: string, unitOf: (variantId: string) => UnitType): Promise<SaleRow[]> {
  const local = await db.sales.where('original_sale_id').equals(saleId).toArray();
  if (!navigator.onLine) return local;
  const { data, error } = await supabase.from('sales').select(SELECT).eq('original_sale_id', saleId);
  if (error) return local;
  const byId = new Map(local.map((s) => [s.id, s]));
  for (const r of data as unknown as ServerSale[]) if (!byId.has(r.id)) byId.set(r.id, fromServer(r, unitOf));
  return [...byId.values()];
}
